import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useCallback } from 'react'
import useSWR from 'swr'
import { TaggableUser } from 'types'
import { UserID } from 'types/fde'

const ALERTS_USERS_PATH = '/api/v1/notifications/users'

interface AlertsMentionableUser {
  id: string
  name?: string | null
  email?: string | null
  active?: boolean | null
}

/**
 * Fetches mentionable users from the cantilever-alerts service.
 * Used for @-mention functionality in contexts where org/site Rolodex
 * user lists are not the appropriate source (e.g. libra comments).
 */
export const useAlertsMentionableUsers = (): {
  data: TaggableUser[]
  isLoading: boolean
  error: string | null
} => {
  const { ALERTS_URL } = useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()

  const fetcher = useCallback(async () => {
    const response = await authorizedAxios.get<AlertsMentionableUser[]>(
      `${ALERTS_URL}${ALERTS_USERS_PATH}`
    )
    return response.data
      .filter((u) => u.active !== false)
      .map(
        (u): TaggableUser => ({
          id: u.id as UserID,
          name: u.name ?? '',
          email: u.email ?? '',
        })
      )
  }, [ALERTS_URL, authorizedAxios])

  const { data, isLoading, error } = useSWR(
    ALERTS_URL ? `${ALERTS_URL}${ALERTS_USERS_PATH}` : null,
    fetcher,
    { revalidateOnFocus: false }
  )

  return {
    data: data ?? [],
    isLoading,
    error: error ? String(error) : null,
  }
}
